import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
  useSyncExternalStore,
} from 'react';

const createSSEStore = () => {
  let latestMessage = null;
  const latestMessagesByTarget = new Map();
  const listeners = new Set();
  const targetListeners = new Map();

  const notify = (targetSet) => {
    targetSet.forEach((listener) => listener());
  };

  return {
    publish(message) {
      latestMessage = message;
      if (message?.target) {
        latestMessagesByTarget.set(message.target, message);
      }

      notify(listeners);

      const targetSet = message?.target ? targetListeners.get(message.target) : null;
      if (targetSet) {
        notify(targetSet);
      }
    },
    getSnapshot() {
      return latestMessage;
    },
    getTargetSnapshot(target) {
      return latestMessagesByTarget.get(target) ?? null;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    subscribeTarget(target, listener) {
      if (!targetListeners.has(target)) {
        targetListeners.set(target, new Set());
      }

      const targetSet = targetListeners.get(target);
      targetSet.add(listener);

      return () => {
        targetSet.delete(listener);
        if (targetSet.size === 0) {
          targetListeners.delete(target);
        }
      };
    },
  };
};

function useSSEControl(url, store) {
  const [sseError, setSseError] = useState(null);
  const [reconnectTrigger, setReconnectTrigger] = useState(false);

  const connect_URL = url; 
  const hasStarted = useRef(false);
  const hasEnded = useRef(false);
  const eventSourceRef = useRef(null);

  const retryCount = useRef(0);
  const retryTimeout = useRef(null);
  const messageQueueRef = useRef([]);
  const processingQueueRef = useRef(false);
  const queueTimeoutRef = useRef(null);
  const messageSeqRef = useRef(0);
  const lastRoundRatePayloadRef = useRef(null);

  const MAX_RETRY = 10;
  const BASE_DELAY = 1000; // 1秒
  const RATE_LIMITED_INITIAL_DELAY = 5000; // 429時は最初から長めの猶予

  const handleReconnection = () => {
    setReconnectTrigger(prev => !prev);
  };

  const closeConnection = (sse = eventSourceRef.current) => {
    if (!sse) {
      return;
    }

    sse.close();
    if (eventSourceRef.current === sse) {
      eventSourceRef.current = null;
    }
  };

  const handleError = (status, message) => {
    if (retryTimeout.current) {
      return;
    }

    if (retryCount.current >= MAX_RETRY) {
      setSseError(new Error(message || "SSE connection failed. Please try again later."));
      return;
    }

    const isRateLimited = status === 429;
    // 429が返った場合は初回からディレイを長く取り、リトライ頻度を抑える
    const baseDelay = isRateLimited ? RATE_LIMITED_INITIAL_DELAY : BASE_DELAY;

    // Exponential backoff
    const delay = Math.min(baseDelay * 2 ** retryCount.current, 30000);

    retryCount.current++;

    retryTimeout.current = setTimeout(() => {
      retryTimeout.current = null;
      handleReconnection();
    }, delay);
  };

  const emitQueuedMessage = () => {
    const nextMessage = messageQueueRef.current.shift();
    if (!nextMessage) {
      processingQueueRef.current = false;
      return;
    }

    messageSeqRef.current += 1;
    store.publish({
      ...nextMessage,
      __seq: messageSeqRef.current,
    });

    queueTimeoutRef.current = setTimeout(emitQueuedMessage, 0);
  };

  const enqueueMessage = (message) => {
    if (message.type === 'direct' && message.action === 'update' && message.target === 'roundRate') {
      const payloadKey = JSON.stringify(message.payload ?? null);
      if (payloadKey === lastRoundRatePayloadRef.current) {
        return;
      }

      lastRoundRatePayloadRef.current = payloadKey;
    }

    messageQueueRef.current.push(message);
    if (processingQueueRef.current) {
      return;
    }

    processingQueueRef.current = true;
    queueTimeoutRef.current = setTimeout(emitQueuedMessage, 0);
  };

  const addEventListeners = (sse) => {
    sse.onmessage = (evt) => {
      if (eventSourceRef.current !== sse) {
        return;
      }

      try {
        const raw = JSON.parse(evt.data);

        if (raw?.type === 'error') {
          closeConnection(sse);
          handleError(raw?.status, raw?.message);
          return;
        }

        // 必須項目の検査
        if (!raw.type || !raw.action || !raw.target) {
          throw new Error(`Missing required fields in SSE message: ${JSON.stringify(raw)}`);
        }

        const message = {
          type: raw.type,
          action: raw.action,
          target: raw.target,
          payload: raw.payload ?? null,  // payloadだけはオプション扱い
        };

        enqueueMessage(message);
        console.log('reception SSE', message);
      } catch (e) {
        console.error('Invalid SSE message:', e.message);
      }
    };
  };

  const initiateConnection = () => {
    closeConnection();
    hasEnded.current = false;

    const sse = new EventSource(connect_URL);
    eventSourceRef.current = sse;

    sse.onerror = () => {
      if (eventSourceRef.current !== sse) {
        return;
      }

      closeConnection(sse);
      handleError();
    };

    sse.addEventListener('START', () => {
      if (eventSourceRef.current !== sse) {
        return;
      }

      hasStarted.current = true;
      retryCount.current = 0;
    });

    sse.addEventListener('END', () => {
      if (eventSourceRef.current !== sse) {
        return;
      }

      hasEnded.current = true;
      closeConnection(sse);
    });

    addEventListeners(sse);
  };

  useEffect(() => {
    initiateConnection();
    return () => {
      hasEnded.current = true;
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
        retryTimeout.current = null;
      }
      closeConnection();
      if (queueTimeoutRef.current) {
        clearTimeout(queueTimeoutRef.current);
      }
    };
  }, [url, reconnectTrigger]);

  return { error: sseError, sseError };
}

const emptySSEStore = createSSEStore();
const SSEContext = createContext(null);

export const useSSEData = () => {
  const context = useContext(SSEContext);
  const store = context?.store ?? emptySSEStore;
  const sseData = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );

  return {
    sseData,
    sseError: context?.sseError ?? null,
    error: context?.sseError ?? null,
  };
};

export const useSSETarget = (target) => {
  const context = useContext(SSEContext);
  const store = context?.store ?? emptySSEStore;

  const subscribe = useCallback(
    (listener) => store.subscribeTarget(target, listener),
    [store, target],
  );
  const getSnapshot = useCallback(
    () => store.getTargetSnapshot(target),
    [store, target],
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

export const useSSEError = () => {
  const context = useContext(SSEContext);
  return context?.sseError ?? null;
};

export const SSEProvider = ({ url, children }) => {
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createSSEStore();
  }

  const { error: sseError } = useSSEControl(url, storeRef.current);
  const contextValue = useMemo(
    () => ({
      store: storeRef.current,
      sseError,
      error: sseError,
    }),
    [sseError],
  );

  return (
    <SSEContext.Provider value={contextValue}>
      {children}
    </SSEContext.Provider>
  );
};
